
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../integrations/supabase/client';
import { BookingData } from '../BookingWizard';
import { Car, Crown, Truck, ChevronRight, Shield, Clock, Users, Briefcase } from 'lucide-react';
import type { Database } from '../../../integrations/supabase/types';

type Vehicle = Database['public']['Tables']['vehicles']['Row'];

interface VehicleStepProps {
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
}

const VehicleStep = ({ bookingData, onUpdate, onNext }: VehicleStepProps) => {
  const { data: vehicles, isLoading, error } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .order('price_per_km');
      
      if (error) {
        console.error('Error fetching vehicles:', error);
        throw error;
      }
      return data as Vehicle[];
    }
  });

  const calculateFare = (pricePerKm: number) => {
    if (!bookingData.routeInfo) return 0;
    return (bookingData.routeInfo.distance * pricePerKm);
  };

  const getVehicleIcon = (type: string) => {
    switch (type) {
      case 'luxury':
        return Crown;
      case 'van':
        return Truck;
      default:
        return Car;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    console.error('Vehicles error:', error);
    return (
      <div className="text-center py-4">
        <p className="text-gray-600">Unable to load vehicles. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm bg-gray-50 p-3 rounded-lg flex items-center justify-between">
        <span>Distance: <strong>{bookingData.routeInfo?.distance.toFixed(1)} km</strong></span>
        <span>Duration: <strong>{Math.round(bookingData.routeInfo?.duration || 0)} min</strong></span>
      </div>

      <div className="space-y-5">
        {vehicles?.map((vehicle) => {
          const fare = calculateFare(vehicle.price_per_km);
          const isSelected = bookingData.selectedVehicle?.id === vehicle.id;
          const Icon = getVehicleIcon(vehicle.type);

          return (
            <div
              key={vehicle.id}
              className={`rounded-lg overflow-hidden transition-all duration-200 ${
                isSelected ? 'ring-2 ring-primary shadow-lg' : 'border border-gray-200 hover:border-primary/30'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 h-full">
                <div className="bg-gray-100 h-[180px] md:h-auto relative overflow-hidden">
                  <img 
                    src={vehicle.image_url || '/placeholder.svg'} 
                    alt={vehicle.name}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                  />
                  {vehicle.type === 'luxury' && (
                    <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Premium
                    </div>
                  )}
                </div>
                
                <div className="p-4 md:col-span-2 flex flex-col h-full">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg flex items-center">
                        <Icon className="w-5 h-5 mr-1.5 text-primary" />
                        {vehicle.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{vehicle.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-primary">€{fare.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">€{vehicle.price_per_km}/km</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-700">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1 text-gray-500" />
                      <span>Up to {vehicle.capacity} passengers</span>
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 mr-1 text-gray-500" />
                      <span>{vehicle.luggage_capacity} luggage</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-gray-500" />
                      <span>24/7 service</span>
                    </div>
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 mr-1 text-gray-500" />
                      <span>Free cancellation (24h)</span>
                    </div>
                  </div>
                  
                  <div className="mt-auto pt-3">
                    <button
                      onClick={() => {
                        onUpdate({ selectedVehicle: vehicle });
                        onNext();
                      }}
                      className={`w-full py-2.5 rounded-lg flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-primary text-white' 
                          : 'bg-gray-100 hover:bg-primary/10 text-gray-800'
                      }`}
                    >
                      <span>{isSelected ? 'Selected' : 'Select'}</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleStep;
